import React, {useMemo, useState} from 'react';
import {Box, InputAdornment, Stack, TextField} from '@mui/material';
import {
  Body,
  Headline,
  Icon,
  Tooltip,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {scrollbarMixins} from '@/styles/mixins';
import LanguageCode from './LanguageCode';

const checkboxCheckedSrc = '/assets/svg/checkbox-checked.svg';
const checkboxUncheckedSrc = '/assets/svg/checkbox-unchecked.svg';

const DEFAULT_COLUMNS_COUNT = 2;

export interface LanguageOption {
  id: string;
  label: string;
  code: string;
}

export class LanguageGroup {
  constructor(
    public name: string,
    public languages: LanguageOption[],
    public columns: number
  ) {}
}

export interface LanguageSelectorProps {
  testID?: string;
  languageGroups: LanguageGroup[];
  selectedLanguages: string[];
  setSelectedLanguages: (langs: string[]) => void;
  isLoading?: boolean;
  title?: string;
  searchQueryCleared?: () => void;
  searchQueryChanged?: (query: string) => void;
  clearAllPressed?: () => void;
  additionalHeaderContent?: React.ReactNode;
  customSelectedLanguagesCounter?: React.ReactNode;
  showLanguageCodes?: boolean;
  hideCounterWhenNonSelected?: boolean;
}

function DefaultSelectedLanguagesCounter({
  selectedLanguages,
  onClearAll,
}: {
  selectedLanguages: string[];
  onClearAll: () => void;
}) {
  const selectedLanguagesString = selectedLanguages.join(', ');
  const selectedLanguagesCount = selectedLanguages.length;

  return (
    <Stack direction="row" alignItems="center">
      <Tooltip
        title={selectedLanguagesString}
        arrow
        type="light"
        placement="top"
        sx={{
          maxWidth: '268px',
          padding: '10px 12px',
          textAlign: 'center',
          fontSize: '16px',
        }}
      >
        <Box>
          <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
            {translate('x_languages_selected.', {
              count: selectedLanguagesCount,
            })}
          </Body>
        </Box>
      </Tooltip>
      {selectedLanguagesCount > 0 && (
        <Body
          size="md"
          onClick={onClearAll}
          sx={{
            textDecoration: 'underline',
            color: designSystemToken('semantic.fg.primary'),
            cursor: 'pointer',
            marginLeft: 1,
          }}
        >
          {translate('Clear all')}
        </Body>
      )}
    </Stack>
  );
}

function shouldShowCounter(
  hideCounterWhenNonSelected: boolean,
  selectedLanguagesCount: number
): boolean {
  return selectedLanguagesCount > 0 || !hideCounterWhenNonSelected;
}

function LanguageSelector({
  testID = 'language-selector',
  languageGroups = [],
  selectedLanguages,
  setSelectedLanguages,
  isLoading = false,
  title = 'Choose languages',
  searchQueryCleared,
  searchQueryChanged,
  clearAllPressed,
  additionalHeaderContent,
  customSelectedLanguagesCounter,
  showLanguageCodes = false,
  hideCounterWhenNonSelected = false,
}: LanguageSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const allLanguages = useMemo(() => {
    const seenIds = new Set<string>();
    const uniqueLanguages: LanguageOption[] = [];

    languageGroups.forEach((group) => {
      group.languages.forEach((lang) => {
        if (!seenIds.has(lang.id)) {
          seenIds.add(lang.id);
          uniqueLanguages.push(lang);
        }
      });
    });

    return uniqueLanguages;
  }, [languageGroups]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery) {
      return languageGroups;
    }

    const filteredLanguages = allLanguages.filter((lang) =>
      lang.label?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return filteredLanguages.length > 0
      ? [
          new LanguageGroup(
            'search-results',
            filteredLanguages,
            languageGroups[0]?.columns || DEFAULT_COLUMNS_COUNT
          ),
        ]
      : [];
  }, [allLanguages, searchQuery]);

  const handleSelect = (langId: string) => {
    if (selectedLanguages.includes(langId)) {
      setSelectedLanguages(selectedLanguages.filter((l) => l !== langId));
    } else {
      setSelectedLanguages([...selectedLanguages, langId]);
    }
  };

  const handleClearAll = () => {
    clearAllPressed?.();
    setSelectedLanguages([]);
  };

  const showCounter = shouldShowCounter(
    hideCounterWhenNonSelected,
    selectedLanguages.length
  );

  const showCustomCounter = showCounter && customSelectedLanguagesCounter;
  const showDefaultCounter = showCounter && !customSelectedLanguagesCounter;

  const renderLanguageOption = (option: LanguageOption) => {
    const isSelected = selectedLanguages.includes(option.id);

    return (
      <Stack
        key={option.id}
        direction="row"
        onClick={() => handleSelect(option.id)}
        sx={{
          'cursor': 'pointer',
          'gap': '8px',
          'height': '24px',
          'justifyContent': 'space-between',
          'width': '100%',
          'minWidth': 0,
          'alignItems': 'center',
          '&:hover': {
            background: designSystemToken('semantic.bg.primary'),
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{gap: '8px', flex: 1, minWidth: 0}}
        >
          <img
            src={isSelected ? checkboxCheckedSrc : checkboxUncheckedSrc}
            alt=""
          />
          <Body
            size="lg"
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {option.label}
          </Body>
        </Stack>
        {showLanguageCodes && option.code && (
          <LanguageCode code={option.code} isSelected={isSelected} />
        )}
      </Stack>
    );
  };

  return (
    <Box
      data-testid={testID}
      sx={{
        width: '100%',
        padding: '0px 0px 0px 24px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Stack
        paddingRight="24px"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '10px',
        }}
      >
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '28px',
            width: '100%',
          }}
        >
          <Headline
            size="sm"
            color={designSystemToken('semantic.fg.primary')}
            sx={{textAlign: 'left'}}
          >
            {translate(title)}
          </Headline>
          <Box display="flex" alignItems="center" gap="8px">
            {showCustomCounter && customSelectedLanguagesCounter}
            {additionalHeaderContent}
          </Box>
        </Stack>
        {showDefaultCounter && (
          <DefaultSelectedLanguagesCounter
            selectedLanguages={selectedLanguages}
            onClearAll={handleClearAll}
          />
        )}
      </Stack>
      <TextField
        sx={{
          'paddingRight': '24px',
          '& .MuiInputBase-root': {
            borderRadius: '12px',
            backgroundColor: designSystemToken('semantic.bg.primary'),
            height: '40px',
          },
          '& .MuiInputBase-input': {
            fontSize: '14px',
          },
        }}
        placeholder={translate('Search languages')}
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          searchQueryChanged?.(e.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon
                name="Actions-Search-Generic"
                size="medium"
                appearance="primary"
              />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment
              position="end"
              onClick={() => {
                setSearchQuery('');
                searchQueryCleared?.();
              }}
            >
              <Icon
                name="Actions-Close-Small"
                size="medium"
                appearance="primary"
                style={{cursor: 'pointer'}}
              />
            </InputAdornment>
          ),
        }}
      />
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          ...scrollbarMixins.overrideScrollbar(),
          display: 'flex',
          flexDirection: 'column',
          gap: '28px',
          paddingRight: '24px',
        }}
      >
        {(() => {
          if (isLoading) {
            return <Body size="md">{translate('Loading...')}</Body>;
          }

          const totalLanguages = filteredGroups.reduce(
            (total, group) => total + group.languages.length,
            0
          );

          if (totalLanguages === 0) {
            return <Body size="md">{translate('No languages found')}</Body>;
          }

          return (
            <>
              {filteredGroups.map((group) => {
                if (group.languages.length === 0) return null;

                const {columns} = group;

                return (
                  <Box
                    key={group.name}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: searchQuery ? '0px' : '16px',
                    }}
                  >
                    {!searchQuery && group.name && (
                      <Body
                        size="sm"
                        color={designSystemToken('semantic.fg.secondary')}
                      >
                        {translate(group.name)}
                      </Body>
                    )}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                        gap: '28px 72px',
                        width: '100%',
                        boxSizing: 'border-box',
                        paddingBottom: '24px',
                      }}
                    >
                      {group.languages.map(renderLanguageOption)}
                    </Box>
                  </Box>
                );
              })}
            </>
          );
        })()}
      </Box>
    </Box>
  );
}

export default LanguageSelector;
